/**
 * The 仮領収書 printed while offline (T07-33): pos-service's **template** (header + footer bytes +
 * labels, downloaded while online — `GET /pos/registers/current/offline-receipt`) with the lines of
 * this sale in between. The tablet fills in placeholders only:
 *
 * ```
 * [header — shop, 登録番号, 「仮領収書」 (server bytes)]
 * 仮番号 T-R01d1-7              2026-09-28 12:34
 * ブラッキーV SA  Rank A                  ¥7,000
 * BOX 151 ×2                             ¥11,000
 * 合計                                  ¥18,000
 * 現金 お預り                           ¥20,000
 * お釣り                                  ¥2,000
 * [footer — 「税額は正式領収書に記載」 … cut (server bytes)]
 * ```
 *
 * Lines are drawn as a raster image with the tablet's own Noto Sans JP (any ESC/POS printer, any
 * script) when the template is raster and the browser has a canvas; else ASCII text lines in the
 * printer's font (codes + amounts — the names cannot be encoded without the printer's code page).
 * No 消費税 line: the tax is decided by tax-service when the sale is uploaded.
 */
import type { OfflineReceiptTemplate } from "../types";
import { base64ToBytes } from "./escpos";

export interface ReceiptRow {
  left: string;
  right?: string;
  bold?: boolean;
  big?: boolean;
}

export interface OfflineReceiptData {
  tempReceiptNo: string;
  time: string;
  staffName: string | null;
  lines: Array<{ title: string; code: string | null; qty: number; amount: number }>;
  total: number;
  tendered: number;
  change: number;
  yen: (amount: number) => string;
}

/** Renders rows into ESC/POS raster bytes (`GS v 0`) — null when the browser cannot draw. */
export type RowRasterizer = (rows: ReceiptRow[], dots: number) => Uint8Array | null;

const BAND_ROWS = 256;
const FONT_PX = 24;
const GAP = 6;

function label(template: OfflineReceiptTemplate, key: string): string {
  const values = template.labels[key];
  if (!values) {
    return key;
  }
  const ja = values.ja ?? key;
  const other = values[template.locale];
  return other && template.locale !== "ja" ? `${ja} ${other}` : ja;
}

export function receiptRows(
  template: OfflineReceiptTemplate,
  data: OfflineReceiptData,
): ReceiptRow[] {
  const rows: ReceiptRow[] = [
    { left: `${label(template, "offlineTempNo")} ${data.tempReceiptNo}`, bold: true },
    { left: data.time, right: data.staffName ?? "" },
    { left: "" },
  ];
  for (const line of data.lines) {
    const qty = line.qty > 1 ? ` ×${String(line.qty)}` : "";
    rows.push({ left: `${line.title}${qty}`, right: data.yen(line.amount) });
    if (line.code) {
      rows.push({ left: `  ${line.code}` });
    }
  }
  rows.push(
    { left: "" },
    { left: label(template, "total"), right: data.yen(data.total), bold: true, big: true },
    {
      left: `${label(template, "cashOnly")} ${label(template, "tendered")}`,
      right: data.yen(data.tendered),
    },
    { left: label(template, "change"), right: data.yen(data.change) },
  );
  return rows;
}

/** 1-bit rows (MSB first, 1 = black) → `GS v 0` bands of ≤ 256 rows. */
export function rasterBands(bits: Uint8Array, widthBytes: number, height: number): Uint8Array {
  const out: number[] = [];
  for (let start = 0; start < height; start += BAND_ROWS) {
    const rows = Math.min(BAND_ROWS, height - start);
    out.push(0x1d, 0x76, 0x30, 0x00, widthBytes & 0xff, widthBytes >> 8, rows & 0xff, rows >> 8);
    const from = start * widthBytes;
    for (let i = from; i < from + rows * widthBytes; i += 1) {
      out.push(bits[i] ?? 0);
    }
  }
  return Uint8Array.from(out);
}

/** RGBA pixels → 1-bit rows (dark = black), the printer's raster format. */
export function thresholdBits(rgba: Uint8ClampedArray, width: number, height: number): Uint8Array {
  const widthBytes = Math.ceil(width / 8);
  const bits = new Uint8Array(widthBytes * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const lum =
        0.299 * (rgba[i] ?? 255) + 0.587 * (rgba[i + 1] ?? 255) + 0.114 * (rgba[i + 2] ?? 255);
      const alpha = rgba[i + 3] ?? 255;
      if (alpha > 127 && lum < 128) {
        const byte = y * widthBytes + (x >> 3);
        bits[byte] = (bits[byte] ?? 0) | (0x80 >> (x & 7));
      }
    }
  }
  return bits;
}

/** The browser's canvas with the app's fonts (Noto Sans JP / Thai are bundled — T07-22). */
export const canvasRasterizer: RowRasterizer = (rows, dots) => {
  if (typeof document === "undefined") {
    return null;
  }
  const canvas = document.createElement("canvas");
  const probe = canvas.getContext("2d");
  if (!probe) {
    return null;
  }
  const lineHeight = (row: ReceiptRow) => (row.big ? FONT_PX * 2 : FONT_PX) + GAP;
  const height = rows.reduce((h, row) => h + lineHeight(row), 8) + 8;
  canvas.width = dots;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, dots, height);
  ctx.fillStyle = "#000";
  ctx.textBaseline = "top";
  let y = 8;
  for (const row of rows) {
    const size = row.big ? FONT_PX * 2 : FONT_PX;
    ctx.font = `${row.bold ? "700" : "400"} ${String(size)}px "Noto Sans JP", "Noto Sans Thai", sans-serif`;
    const right = row.right ?? "";
    const rightWidth = right ? ctx.measureText(right).width : 0;
    let left = row.left;
    while (left && ctx.measureText(left).width > dots - rightWidth - 12) {
      left = left.slice(0, -1);
    }
    ctx.fillText(left, 0, y);
    if (right) {
      ctx.fillText(right, dots - rightWidth, y);
    }
    y += lineHeight(row);
  }
  const pixels = ctx.getImageData(0, 0, dots, height).data;
  return rasterBands(thresholdBits(pixels, dots, height), Math.ceil(dots / 8), height);
};

/** Printer-font fallback: ASCII only (codes, amounts) — `¥` is 0x5C with the Japan charset. */
export function asciiRows(columns: number, data: OfflineReceiptData): Uint8Array {
  const ascii = (text: string) => text.replace(/[^\x20-\x7e¥]/g, "").trim();
  const lines: string[] = [];
  const pad = (left: string, right: string) => {
    const room = Math.max(1, columns - right.length);
    const l = left.length > room - 1 ? left.slice(0, room - 1) : left;
    return l + " ".repeat(Math.max(1, columns - l.length - right.length)) + right;
  };
  lines.push(`No. ${data.tempReceiptNo}`, ascii(data.time));
  for (const line of data.lines) {
    const name = ascii(line.code ?? "") || ascii(line.title) || "item";
    lines.push(pad(`${name}${line.qty > 1 ? ` x${String(line.qty)}` : ""}`, data.yen(line.amount)));
  }
  lines.push(
    pad("TOTAL", data.yen(data.total)),
    pad("CASH", data.yen(data.tendered)),
    pad("CHANGE", data.yen(data.change)),
  );
  const text = `${lines.join("\n")}\n`.replace(/¥/g, "\\");
  return Uint8Array.from([...text].map((c) => c.charCodeAt(0) & 0xff));
}

/** Header + the sale's lines + footer — the bytes to send to the receipt printer. */
export function composeOfflineReceipt(
  template: OfflineReceiptTemplate,
  data: OfflineReceiptData,
  rasterize: RowRasterizer = canvasRasterizer,
): Uint8Array {
  const header = base64ToBytes(template.header);
  const footer = base64ToBytes(template.footer);
  const rows = receiptRows(template, data);
  const body =
    (template.mode === "raster" ? rasterize(rows, template.dots) : null) ??
    asciiRows(template.columns, data);
  const out = new Uint8Array(header.length + body.length + footer.length);
  out.set(header, 0);
  out.set(body, header.length);
  out.set(footer, header.length + body.length);
  return out;
}
